/**
 * ============================================================================
 * ETCHED REFLECTION - Mobile Navigation
 * ============================================================================
 *
 * Handles mobile menu toggle, drawer animation, and overlay
 * Accessible keyboard navigation support
 *
 * ============================================================================
 */

(function() {
  'use strict';

  const MobileNav = {
    // DOM Elements
    toggle: null,
    nav: null,
    overlay: null,
    focusableElements: [],
    firstFocusable: null,
    lastFocusable: null,

    // State
    isOpen: false,

    init() {
      // Get DOM elements
      this.toggle = document.querySelector('.mobile-menu-toggle');
      this.nav = document.querySelector('.mobile-nav');
      this.overlay = document.querySelector('.mobile-nav-overlay');

      if (!this.toggle || !this.nav) {
        return;
      }

      // Bind events
      this.bindEvents();

      // Set initial ARIA states
      this.toggle.setAttribute('aria-expanded', 'false');
      this.nav.setAttribute('aria-hidden', 'true');
    },

    bindEvents() {
      // Toggle button click
      this.toggle.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleMenu();
      });

      // Overlay click to close
      if (this.overlay) {
        this.overlay.addEventListener('click', () => {
          this.closeMenu();
        });
      }

      // Close on Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.closeMenu();
          this.toggle.focus();
        }
      });

      // Trap focus within menu when open
      this.nav.addEventListener('keydown', (e) => {
        if (e.key === 'Tab') {
          this.handleTabKey(e);
        }
      });

      // Close menu on window resize (if desktop breakpoint reached)
      window.addEventListener('resize', window.EtchedReflection?.debounce(() => {
        if (window.innerWidth >= 1024 && this.isOpen) {
          this.closeMenu();
        }
      }, 250));

      // Handle dropdown toggles
      this.nav.querySelectorAll('[data-mobile-dropdown-toggle]').forEach(toggle => {
        toggle.addEventListener('click', (e) => {
          e.preventDefault();
          const item = toggle.closest('.mobile-nav__item--has-dropdown');
          if (item) {
            item.classList.toggle('is-open');
            const isOpen = item.classList.contains('is-open');
            toggle.setAttribute('aria-expanded', isOpen);
          }
        });
      });

      // Close menu when clicking nav links (but not dropdown toggles)
      this.nav.querySelectorAll('.mobile-nav__link:not([data-mobile-dropdown-toggle])').forEach(link => {
        link.addEventListener('click', () => {
          this.closeMenu();
        });
      });

      // Close menu when clicking dropdown links
      this.nav.querySelectorAll('.mobile-nav__dropdown-link').forEach(link => {
        link.addEventListener('click', () => {
          this.closeMenu();
        });
      });
    },

    toggleMenu() {
      if (this.isOpen) {
        this.closeMenu();
      } else {
        this.openMenu();
      }
    },

    openMenu() {
      this.isOpen = true;

      // Update toggle button
      this.toggle.classList.add('is-active');
      this.toggle.setAttribute('aria-expanded', 'true');
      this.toggle.setAttribute('aria-label', 'Close menu');

      // Show nav
      this.nav.classList.add('is-open');
      this.nav.setAttribute('aria-hidden', 'false');

      // Show overlay
      if (this.overlay) {
        this.overlay.classList.add('is-visible');
        this.overlay.setAttribute('aria-hidden', 'false');
      }

      // Prevent body scroll
      document.body.style.overflow = 'hidden';

      // Update focusable elements and set focus
      this.updateFocusableElements();

      // Focus first focusable element after animation
      setTimeout(() => {
        if (this.firstFocusable) {
          this.firstFocusable.focus();
        }
      }, 100);
    },

    closeMenu() {
      this.isOpen = false;

      // Update toggle button
      this.toggle.classList.remove('is-active');
      this.toggle.setAttribute('aria-expanded', 'false');
      this.toggle.setAttribute('aria-label', 'Open menu');

      // Hide nav
      this.nav.classList.remove('is-open');
      this.nav.setAttribute('aria-hidden', 'true');

      // Hide overlay
      if (this.overlay) {
        this.overlay.classList.remove('is-visible');
        this.overlay.setAttribute('aria-hidden', 'true');
      }

      // Restore body scroll
      document.body.style.overflow = '';
    },

    updateFocusableElements() {
      const focusableSelectors = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])'
      ].join(', ');

      this.focusableElements = Array.from(
        this.nav.querySelectorAll(focusableSelectors)
      ).filter(el => el.offsetParent !== null);

      this.firstFocusable = this.focusableElements[0];
      this.lastFocusable = this.focusableElements[this.focusableElements.length - 1];
    },

    handleTabKey(e) {
      if (!this.isOpen) return;

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === this.firstFocusable) {
          e.preventDefault();
          this.lastFocusable?.focus();
        }
      } else {
        // Tab
        if (document.activeElement === this.lastFocusable) {
          e.preventDefault();
          this.firstFocusable?.focus();
        }
      }
    }
  };

  // Initialize on DOMContentLoaded
  document.addEventListener('DOMContentLoaded', () => {
    MobileNav.init();
  });

  // Expose for external use
  window.EtchedReflection = window.EtchedReflection || {};
  window.EtchedReflection.MobileNav = MobileNav;

})();

